import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import Breadcrumb, { getArticleBreadcrumb } from '@/components/feature/Breadcrumb';

function getCategoryName(slug: string): string {
  return categories.find((c) => c.slug === slug)?.name || slug;
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <main className="min-h-screen pt-20 pb-14 px-4 md:px-6">
        <div className="mx-auto max-w-4xl text-center py-20">
          <h2 className="text-xl font-heading font-semibold text-foreground-700 mb-3">文章未找到</h2>
          <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer">
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const categoryName = getCategoryName(article.category);
  const breadcrumbItems = getArticleBreadcrumb(article.category, categoryName, article.title);

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 3);

  const jsonLdArticle = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    image: article.coverImage,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: '最新发布页',
      logo: {
        '@type': 'ImageObject',
        url: 'https://gdbtui7.cn/logo.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://gdbtui7.cn/article/${article.id}`,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLdArticle)}</script>
      <main className="min-h-screen pt-20 pb-14">
        <article className="mx-auto max-w-4xl px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <div className="mb-6">
            <Link
              to={`/category/${article.category}`}
              className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700 hover:bg-accent-200 transition-colors cursor-pointer whitespace-nowrap mb-4"
            >
              {categoryName}
            </Link>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {article.publishDate}
              </span>
            </div>
          </div>

          <div className="relative aspect-[16/9] rounded-lg overflow-hidden mb-8">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover"
              title={`${article.title} - 最新发布页`}
            />
          </div>

          <div
            className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
              prose-h2:text-xl md:prose-h2:text-2xl prose-h2:font-heading prose-h2:font-bold prose-h2:text-foreground-950 prose-h2:mt-10 prose-h2:mb-4
              prose-h3:text-lg md:prose-h3:text-xl prose-h3:font-heading prose-h3:font-semibold prose-h3:text-foreground-900 prose-h3:mt-8 prose-h3:mb-3
              prose-p:text-foreground-700 prose-p:leading-loose prose-p:mb-4
              prose-ul:list-disc prose-ul:pl-6 prose-ul:text-foreground-700 prose-ul:leading-loose prose-li:mb-1
              prose-strong:text-foreground-900 prose-strong:font-semibold"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-10 pt-8 border-t border-background-200/70">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-foreground-600 font-medium">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full text-xs bg-background-100 text-foreground-600"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <section className="mt-12 pt-10 border-t border-background-200/70">
              <h3 className="text-xl font-heading font-bold text-foreground-950 mb-6">
                相关推荐
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.id}`}
                    className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-background-300/80 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative aspect-[16/9] overflow-hidden">
                      <img
                        src={related.coverImage}
                        alt={related.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        title={related.title}
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="text-sm font-heading font-semibold text-foreground-950 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                        {related.title}
                      </h4>
                      <p className="text-xs text-foreground-500 mt-2">{related.publishDate}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
    </>
  );
}